import React from 'react'
import cx from 'classnames'
import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { Loader, Skeleton } from '@manychat/manyui'

import { FIELD_LIST_WIDTH } from 'common/fields/constants'
import { FieldGroupId } from 'common/fields/entities/enums'
import FieldSearch from 'common/fields/linking/components/FieldSearch'
import { fetchFieldValues } from 'common/filter/actions/filtersActions'
import { ConditionTagFieldDescription, ConditionTagPickerCondition } from 'common/filter/interfaces'
import { getFilterValues } from 'common/filter/selectors/filterFieldsSelectors'
import { useDidMountEffect } from 'utils/commonHooks'

import { getNumberedId, makeTagField } from './helpers'

import cm from './ConditionTagPicker.module.css'

export enum ConditionValueOperator {
  IS = 'IS',
  IS_NOT = 'IS_NOT',
}

interface ConditionTagPickerProps {
  condition: ConditionTagPickerCondition
  fieldDescription: ConditionTagFieldDescription
  onChange: (event: { target: { value: number } }, closeModal: boolean) => void
}

export const ConditionTagPicker = ({
  fieldDescription,
  condition,
  onChange,
}: ConditionTagPickerProps) => {
  const dispatch = useAppDispatch()
  const filterValues = useAppSelector(getFilterValues)
  const tags = filterValues?.items
  const isFetching = filterValues?.fetching

  const tagFields = React.useMemo(() => {
    return tags?.map(makeTagField) ?? []
  }, [tags])

  const pickerStyle = { width: FIELD_LIST_WIDTH }

  useDidMountEffect(() => {
    dispatch(fetchFieldValues(fieldDescription.id, ''))
  })

  const handleSelectTag = React.useCallback(
    (tagId: string) => {
      const tagNumberedId = getNumberedId(FieldGroupId.TAG, tagId)
      onChange({ target: { value: tagNumberedId } }, true)
    },
    [onChange],
  )

  const handleRenderTagOption = React.useCallback(
    (option: { value?: string }) => {
      const tagNumberedId = getNumberedId(FieldGroupId.TAG, option.value)
      const tag = tags?.find((tag) => tag.value === tagNumberedId)
      const isSelected = tagNumberedId === condition.value
      return (
        <div className={cx(cm.tagOption, 'd-flex')}>
          <div className={cx(cm.tagOptionLabel, { 'text-subheading text-ellipsis': isSelected })}>
            {tag?.label}
          </div>
          <div className="text-secondary flex-shrink-0 flex-grow-1 text-right">{tag?.count}</div>
        </div>
      )
    },
    [condition.value, tags],
  )

  if (isFetching) {
    return (
      <div style={pickerStyle}>
        <Skeleton.Multiplier quantity={5}>
          <Skeleton.Text height={10} className="m-y-xs m-x" />
        </Skeleton.Multiplier>
        <Loader size={32} className="m-b" />
      </div>
    )
  }

  return (
    <div style={pickerStyle}>
      <FieldSearch
        fields={tagFields}
        onSelect={handleSelectTag}
        showIcon={false}
        renderOption={handleRenderTagOption}
      />
    </div>
  )
}
